import {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {Auth} from '@aws-amplify/auth';
import {Cache} from 'aws-amplify';
import {Account} from '../../accounts/types';
import {checkAccountSession} from '../../account-sessions/services/AccountSessionService';
import returnToDomainNames from '../../../config/return-to-domain-names';

interface OAuth2Params {
    redirectUrl: string | null;
    state: string | null;
    clientId: string | null;
}

export const useOAuth2Authorization = (accounts: Account[], params: OAuth2Params) => {
    const navigate = useNavigate();

    const findAccountByClientId = (clientId: string | null): Account | undefined => {
        return accounts.find(acc => acc.appClientId === clientId);
    };

    const validateRedirectUrl = (redirectUrl: string | null): void => {
        const allowedRedirectUrls = returnToDomainNames;
        const check = false;
        if (check && !allowedRedirectUrls.includes(redirectUrl as string)) {
            throw new Error(`Invalid redirect url ${redirectUrl}`);
        }
    };

    const buildReturnUrl = (redirectUrl: string | null, idToken: string, accessToken: string, state: string | null): string => {
        return `${redirectUrl}#id_token=${idToken}&access_token=${accessToken}&token_type=Bearer&expires_in=3600&state=${state}`;
    };

    const redirectToApp = (redirectUrl: string | null, idToken: string, accessToken: string, state: string | null): void => {
        const returnUrl = buildReturnUrl(redirectUrl, idToken, accessToken, state);
        Cache.removeItem('authorization');
        window.location.assign(returnUrl);
    };

    const handleAuthorizationFailure = async (): Promise<void> => {
        console.log('Session invalid, expired, or does not match account');
        await Auth.signOut({global: false});
        navigate('/');
    };

    const handleAuthorizationError = async (error: unknown): Promise<void> => {
        console.log('Authorization error:', error);
        try {
            await Auth.signOut({global: false});
        } catch (signOutError) {
            console.log('Sign out error:', signOutError);
        }
        navigate('/');
    };

    useEffect(() => {
        const authorizeWithSession = async () => {
            if (accounts.length === 0) {
                return;
            }

            const {redirectUrl, state, clientId} = params;

            const account = findAccountByClientId(clientId);

            if (!account) {
                console.log('No account found for client_id:', clientId);
                navigate('/');
                return;
            }

            try {
                const sessionInfo = await checkAccountSession(account);

                if (sessionInfo.isActive && !sessionInfo.isExpired && sessionInfo.session) {
                    const idToken = sessionInfo.session.getIdToken().getJwtToken();
                    const accessToken = sessionInfo.session.getAccessToken().getJwtToken();

                    validateRedirectUrl(redirectUrl);
                    redirectToApp(redirectUrl, idToken, accessToken, state);
                    return;
                }

                await handleAuthorizationFailure();
            } catch (error) {
                await handleAuthorizationError(error);
            }
        };

        authorizeWithSession();
    }, [accounts, params.redirectUrl, params.state, params.clientId, navigate]);
};
